import { inject, singleton } from 'tsyringe';
import { ContentRecommendationUserAction } from 'app-core/src/constants/content-recommendation-user-action.enum';
import { TrackEvent } from 'app-core/src/models/tracking-event.model';
import { ConditionsSummary } from './models/conditions-summary.model';
import { ConditionsResult } from './models/conditions-result.model';
import { NotificationAnalyticsTracker } from './notification-analytics-tracker';
import { ABTestParams } from './models/ab-test-params.model';
import { NoRecommendationsReasons } from '../content-recommendation-service/no-recommendations-reasons.enums';
import { LoggerService } from '../logger.service';
import { PersistentStorageService } from '../persistent-storage-service/persistent-storage.service';

// -----------------------------------------------------------------------------
export interface INotificationAnalyticsService {
  reportGlobalConditionResult(conditionsResult: ConditionsResult);
  reportApplicationConditionResult(conditionsResult: ConditionsResult);
  reportPortfolioExhausted(gameId: number, originalGameId?: number);
  reportNoGameToRecommend(gameId: number);
  reportNewGameSession(gameId: number);
  reportContentRecommend(
    gameId: number,
    applicationUid: string,
    userAction: any,
    visibilityTimeInSeconds?: number,
    originalGameId?: number,
  );
  reportContentViewEvent(event: TrackEvent);
  reportContentViewError(
    gameId: number,
    applicationUid: string,
    originalGameId?: number,
  );
  reportNotificationABTest(params: ABTestParams);
  setDelegate(delegate: iAnalyticsDelegate);
}

export const kNameOfNotificationAnalyticsService =
  'NotificationAnalyticsService';

export interface iAnalyticsDelegate {
  onContentRecommend(userAction: ContentRecommendationUserAction);
}

const kExcludeSummaryConditions = [NoRecommendationsReasons.PlatformDisabled];
// -----------------------------------------------------------------------------
@singleton()
export class NotificationAnalyticsService
  implements INotificationAnalyticsService
{
  private readonly logger = LoggerService.getCategory(
    kNameOfNotificationAnalyticsService,
  );

  private analyticsDelegate: iAnalyticsDelegate;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly persistentStorageService: PersistentStorageService,
    private readonly notificationAnalyticsTracker: NotificationAnalyticsTracker,
  ) {}

  // ---------------------------------------------------------------------------
  public setDelegate(delegate: iAnalyticsDelegate) {
    this.analyticsDelegate = delegate;
  }

  // ---------------------------------------------------------------------------
  // INotificationAnalyticsService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  reportGlobalConditionResult(conditionsResult: ConditionsResult) {
    if (!conditionsResult.reason) {
      return;
    }

    this.logger.info(
      'ConditionResult update:',
      JSON.stringify(conditionsResult),
    );

    this.setGlobalConditionResult(conditionsResult);
  }

  // ---------------------------------------------------------------------------
  reportApplicationConditionResult(conditionsResult: ConditionsResult) {
    if (!conditionsResult.reason) {
      return;
    }

    if (kExcludeSummaryConditions.includes(conditionsResult.reason)) {
      this.logger.info(
        'Excluded ConditionResult update:',
        JSON.stringify(conditionsResult),
      );

      return;
    }

    this.logger.info(
      'ConditionResult update: ',
      JSON.stringify(conditionsResult),
    );
    this.setApplicationConditionGame(conditionsResult);
  }

  // ---------------------------------------------------------------------------
  reportPortfolioExhausted(gameId: number, originalGameId?: number) {
    this.notificationAnalyticsTracker.sendPortfolioExhausted(
      gameId,
      originalGameId,
    );
  }

  // ---------------------------------------------------------------------------
  reportNoGameToRecommend(gameId: number) {
    const conditionResult: ConditionsResult = {
      gameId,
      reason: NoRecommendationsReasons.NoAppToRecommend,
    };

    this.setGlobalConditionResult(conditionResult);
  }

  // ---------------------------------------------------------------------------
  // Here would be the first analytics event when the game starts,
  // ConditionsSummary event is sent if more then 24hrs passed since the last
  // event, additionally the ConditionsSummary is reset after tracking is sent.
  async reportNewGameSession(gameId: number) {
    await this.sendAndResetConditionsSummaryIfShould();
    this.incrementGameSessionsCounter(gameId);
  }

  // ---------------------------------------------------------------------------
  reportContentViewError(
    gameId: number,
    applicationUid: string,
    originalGameId?: number,
  ) {
    this.notificationAnalyticsTracker.sendAnalyticsError(
      gameId,
      applicationUid,
      originalGameId,
    );
  }

  // ---------------------------------------------------------------------------
  reportContentRecommend(
    gameId: number,
    applicationUid: string,
    userAction: ContentRecommendationUserAction,
    visibilityTimeInSeconds?: number,
    originalGameId?: number,
  ) {
    this.analyticsDelegate?.onContentRecommend(userAction);

    this.notificationAnalyticsTracker.sendContentRecommend(
      gameId,
      applicationUid,
      userAction,
      visibilityTimeInSeconds,
      originalGameId,
    );
  }

  // ---------------------------------------------------------------------------
  reportContentViewEvent(event: TrackEvent) {
    this.notificationAnalyticsTracker.sendContentViewEvent(event);
  }

  // ---------------------------------------------------------------------------
  private incrementGameSessionsCounter(gameId: number) {
    let conditionsSummary =
      this.persistentStorageService.getConditionsSummary();

    if (!conditionsSummary) {
      conditionsSummary = this.generateNewConditionsSummary();
    }

    const globalConditions = conditionsSummary.globalConditions;
    let summary = globalConditions[gameId];
    if (!summary) {
      globalConditions[gameId] = {
        dailyGameSessions: 0,
      };
      summary = globalConditions[gameId];
    }

    globalConditions[gameId].dailyGameSessions++;

    this.persistentStorageService.setConditionsSummary(conditionsSummary);
  }

  // ---------------------------------------------------------------------------
  private resetConditionsSummary() {
    this.persistentStorageService.setConditionsSummary(
      this.generateNewConditionsSummary(),
    );
  }

  // ---------------------------------------------------------------------------
  private generateNewConditionsSummary(): ConditionsSummary {
    return {
      applicationConditions: {},
      globalConditions: {},
      lastSummaryResetTimestamp: Date.now(),
    };
  }

  // ---------------------------------------------------------------------------
  private setGlobalConditionResult(conditionsResult: ConditionsResult) {
    const { gameId, reason } = conditionsResult;
    let conditionsSummary =
      this.persistentStorageService.getConditionsSummary();
    if (!conditionsSummary) {
      conditionsSummary = this.generateNewConditionsSummary();
    }

    const globalConditions = conditionsSummary.globalConditions;
    let gameSummary = globalConditions[gameId];
    if (!gameSummary) {
      globalConditions[gameId] = {
        dailyGameSessions: 0,
      };

      gameSummary = globalConditions[gameId];
    }

    if (!gameSummary[reason]) {
      gameSummary[reason] = 0;
    }

    gameSummary[reason]++;

    this.persistentStorageService.setConditionsSummary(conditionsSummary);
  }

  // ---------------------------------------------------------------------------
  private setApplicationConditionGame(conditionsResult: ConditionsResult) {
    const { gameId, applicationUid, reason } = conditionsResult;
    let conditionsSummary =
      this.persistentStorageService.getConditionsSummary();
    if (!conditionsSummary) {
      conditionsSummary = this.generateNewConditionsSummary();
    }

    let gameSummary = conditionsSummary.applicationConditions[gameId];

    if (!gameSummary) {
      conditionsSummary.applicationConditions[gameId] = {};
      gameSummary = conditionsSummary.applicationConditions[gameId];
    }

    let appSpecificConditions = gameSummary[applicationUid];
    if (!appSpecificConditions) {
      gameSummary[applicationUid] = {
        attempt: 0,
      };
      appSpecificConditions = gameSummary[applicationUid];
    }

    const appReason: number = appSpecificConditions[reason];
    if (!appReason) {
      appSpecificConditions[reason] = 0;
    }

    appSpecificConditions[reason]++;
    appSpecificConditions.attempt++;

    this.persistentStorageService.setConditionsSummary(conditionsSummary);
  }

  // ---------------------------------------------------------------------------
  private async sendAndResetConditionsSummaryIfShould() {
    const conditionsSummary =
      this.persistentStorageService.getConditionsSummary();
    if (!conditionsSummary) {
      return;
    }

    const timePassed = Date.now() - conditionsSummary.lastSummaryResetTimestamp;
    // CR(twolf): 24 * 60 ... turn into constant
    if (timePassed >= 24 * 60 * 60 * 1000) {
      this.logger.info(
        `Sending ConditionResult tracking event, Data: ${JSON.stringify(
          conditionsSummary,
        )}`,
      );

      await this.notificationAnalyticsTracker.sendConditionsSummary(
        conditionsSummary,
      );

      this.resetConditionsSummary();
    }
  }

  // ---------------------------------------------------------------------------
  reportNotificationABTest(params: ABTestParams) {
    this.notificationAnalyticsTracker.ABTestStarted(params);
  }
}
